<template>
  <main id="main" class="main">
    <div class="pagetitle">
      <h1>MESSAGE ROOM</h1>
      <nav>
        <ol class="breadcrumb breadJa">
          <router-link :to="{ name: 'home' }" class="breadcrumb-item">Home</router-link>
          <router-link :to="{ name: 'chat' }" class="breadcrumb-item">Message Room</router-link>
        </ol>
      </nav>
    </div>

    <section class="section">
      <div class="row">
        
        <!-- LEFT COLUMN: Instance Selection & Info -->
        <div class="col-lg-4 col-md-12 mb-3">
          <!-- Instance Selector Card -->
          <div class="card card-chatroom mb-3">
            <div class="card-body">
              <h5 class="card-title card-title-chatroom">
                <i class="bi bi-phone-fill me-2"></i>
                Select Instance
              </h5>
              
              <select 
                class="form-select form-select-chatroom"
                id="instanceSelect"
                v-model="selectedInstance"
                @change="onInstanceChange"
              >
                <option :value="null">-- Choose an instance --</option>
                <option 
                  v-for="instance in onlineInstances" 
                  :key="instance.instanceId"
                  :value="instance"
                >
                  {{ instance.phoneNumber || 'No Number' }} 
                </option>
              </select>
              
              <div v-if="onlineInstances.length === 0" class="alert alert-warning alert-chatroom mt-3 mb-0" role="alert">
                <i class="bi bi-exclamation-triangle me-2"></i>
                <small>No online instances available.</small>
              </div>
            </div>
          </div>

          <!-- Instance Details Card -->
          <div v-if="selectedInstance" class="card card-chatroom">
            <div class="card-body">
              <h5 class="card-title card-title-chatroom text-success">
                <i class="bi bi-check-circle-fill me-2"></i>
                Active Instance
              </h5>
              
              <div class="instance-details-chatroom">
                <div class="detail-item-chatroom">
                  <div class="detail-label-chatroom">
                    <i class="bi bi-telephone-fill text-primary"></i>
                    Phone Number
                  </div>
                  <div class="detail-value-chatroom fw-bold">
                    {{ selectedInstance.phoneNumber || 'N/A' }}
                  </div>
                </div>

                <div class="detail-item-chatroom">
                  <div class="detail-label-chatroom">
                    <i class="bi bi-hash text-primary"></i>
                    Instance ID
                  </div>
                  <div class="detail-value-chatroom text-muted small">
                    {{ selectedInstance.instanceId }}
                  </div>
                </div>

                <div class="detail-item-chatroom">
                  <div class="detail-label-chatroom">
                    <i class="bi bi-circle-fill text-success"></i>
                    Status
                  </div>
                  <div class="detail-value-chatroom">
                    <span class="badge bg-success">Online</span>
                  </div>
                </div>

                <div class="detail-item-chatroom">
                  <div class="detail-label-chatroom">
                    <i class="bi bi-clock-fill text-primary"></i>
                    Connected At
                  </div>
                  <div class="detail-value-chatroom text-muted small">
                    {{ formatDate(selectedInstance.connectedAt) }}
                  </div>
                </div>
              </div>

              <div class="stats-container-chatroom mt-3">
                <div class="stat-box-chatroom">
                  <div class="stat-icon-chatroom">
                    <i class="bi bi-send-fill"></i>
                  </div>
                  <div class="stat-info-chatroom">
                    <div class="stat-label-chatroom">Messages Sent</div>
                    <div class="stat-value-chatroom">{{ messagesSent }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Empty State -->
          <div v-else class="card card-chatroom">
            <div class="card-body text-center py-5">
              <div class="illustration-container">
                <Vue3Lottie  
                  animationLink="/assets/panah-atas.json"
                  :height="198" 
                  :width="198"
                  :loop="true"
                  :autoPlay="true"
                />
              </div>
              <p class="text-muted mb-0">
                Select an instance above<br>to view details
              </p>
            </div>
          </div>
        </div>

        <!-- RIGHT COLUMN: Message Tabs -->
        <div class="col-lg-8 col-md-12">
          <div class="card card-chatroom">
            <div class="card-body">
              <h5 class="card-title card-title-chatroom">
                <i class="bi bi-chat-dots-fill me-2" :style="{ color: themeColor }"></i>
                Send Message
              </h5>

              <!-- Locked State -->
              <div v-if="!selectedInstance" class="locked-state-chatroom">
                <div class="lottie-overlay">
                    <Vue3Lottie  
                    animationLink="/assets/select-intances.json"
                    :height="400" 
                    :width="400"
                    :loop="true"
                    :autoPlay="true"
                    />
                </div>
                <div class="overlay-text">
                    <h4 class="text-muted mb-3">Instance Required</h4>
                    <p class="text-muted mb-0">
                    Please select an online instance from the left panel<br>
                    to start sending messages.
                    </p>
                </div>
              </div>

              <!-- Tabs Content (when instance selected) -->
              <div v-else>
                <!-- MESSAGE TYPE TOGGLE (Personal / Group) -->
                <div class="message-type-toggle-chatroom mb-3">
                  <button 
                    class="toggle-btn-chatroom"
                    :class="{ active: messageType === 'personal' }"
                    @click="messageType = 'personal'"
                  >
                    <i class="bi bi-person-fill"></i>
                    Personal
                  </button>
                  <button 
                    class="toggle-btn-chatroom"
                    :class="{ active: messageType === 'group' }"
                    @click="messageType = 'group'"
                  >
                    <i class="bi bi-people-fill"></i>
                    Group
                  </button>
                </div>

                <!-- GROUP SELECTOR (only show if messageType = group) -->
                <transition name="fade-slide-chatroom">
                  <div v-if="messageType === 'group'" class="mb-3 form-field-chatroom">
                    <label class="form-label fw-bold">
                      <i class="bi bi-chat-left-dots-fill me-1"></i>
                      Select Group
                    </label>
                    <select 
                      class="form-select form-select-chatroom"
                      v-model="selectedGroup"
                    >
                      <option :value="null">-- Choose a group --</option>
                      <option 
                        v-for="group in groups" 
                        :key="group.jid"
                        :value="group"
                      >
                        {{ group.name }} ({{ group.participants }} members)
                      </option>
                    </select>
                    <div v-if="loadingGroups" class="form-text">
                      <span class="spinner-border spinner-border-sm me-1"></span>
                      Loading groups...
                    </div>
                    <div v-if="groups.length === 0 && !loadingGroups" class="form-text text-warning">
                      <i class="bi bi-info-circle me-1"></i>
                      No groups found for this instance
                    </div>
                  </div>
                </transition>


                <!-- Tabs Navigation -->
                <ul class="nav nav-pills-chatroom mb-4" role="tablist">
                  <li class="nav-item" role="presentation">
                    <button 
                      class="nav-link" 
                      :class="{ active: activeTab === 'text' }"
                      @click="activeTab = 'text'"
                      type="button"
                    >
                      <i class="bi bi-chat-text-fill"></i>
                      <span>Text</span>
                    </button>
                  </li>
                  <li class="nav-item" role="presentation">
                    <button 
                      class="nav-link" 
                      :class="{ active: activeTab === 'media' }"
                      @click="activeTab = 'media'"
                      type="button"
                    >
                      <i class="bi bi-image-fill"></i>
                      <span>Media</span>
                    </button>
                  </li>
                </ul>

                <!-- Tab Content -->
                <div class="tab-content">
                  
                  <!-- TEXT MESSAGE TAB -->
                  <div v-show="activeTab === 'text'">
                    <form @submit.prevent="sendTextMessage">
                      <!-- Recipient (only for personal) - dengan transition -->
                      <transition name="fade-slide-chatroom">
                        <div v-if="messageType === 'personal'" class="mb-3 form-field-chatroom">
                          <label for="textRecipient" class="form-label">
                            <i class="bi bi-person-fill me-1"></i>
                            Recipient Phone Number
                          </label>
                          <input 
                            type="text" 
                            class="form-control form-control-chatroom" 
                            id="textRecipient"
                            v-model="textForm.recipient"
                            placeholder="628123456789"
                            required
                          >
                          <div class="form-text">
                            Format: country code + number (e.g., 628123456789)
                          </div>
                        </div>
                      </transition>

                      <!-- Group info (if group selected) -->
                      <transition name="fade-slide-chatroom">
                        <div v-if="messageType === 'group' && selectedGroup" class="alert alert-info alert-chatroom mb-3 form-field-chatroom">
                          <i class="bi bi-people-fill me-2"></i>
                          <strong>Sending to:</strong> {{ selectedGroup.name }} ({{ selectedGroup.participants }} members)
                        </div>
                      </transition>

                      <div class="mb-3">
                        <label for="textMessage" class="form-label">
                          <i class="bi bi-chat-square-text-fill me-1"></i>
                          Message
                        </label>
                        <textarea 
                          class="form-control form-control-chatroom" 
                          id="textMessage"
                          v-model="textForm.message"
                          rows="8"
                          placeholder="Type your message here..."
                          required
                        ></textarea>
                        <div class="d-flex justify-content-between align-items-center mt-2">
                          <div class="form-text mb-0">
                            <i class="bi bi-info-circle me-1"></i>
                            Characters: {{ textForm.message.length }}
                          </div>
                        </div>
                      </div>

                      <div class="d-flex gap-2">
                        <button 
                          type="submit" 
                          class="btn btn-chatroom btn-primary-chatroom flex-grow-1"
                          :disabled="loading || (messageType === 'group' && !selectedGroup)"
                        >
                          <span v-if="loading">
                            <span class="spinner-border spinner-border-sm me-2"></span>
                            Sending...
                          </span>
                          <span v-else>
                            <i class="bi bi-send-fill me-2"></i>
                            Send Message
                          </span>
                        </button>
                        <button 
                          type="button" 
                          class="btn btn-chatroom btn-secondary-chatroom"
                          @click="resetTextForm"
                          :disabled="loading"
                          :style="{ background: themeColor, color: 'white', borderColor: themeColor }"
                        >
                          <i class="bi bi-arrow-counterclockwise"></i>
                        </button>
                      </div>
                    </form>
                  </div>

                  <!-- MEDIA MESSAGE TAB -->
                  <div v-show="activeTab === 'media'">
                    <!-- Media Type Toggle (URL / Upload) -->
                    <div class="media-method-toggle-chatroom mb-3">
                      <button 
                        class="method-btn-chatroom"
                        :class="{ active: mediaMethod === 'upload' }"
                        @click="mediaMethod = 'upload'"
                      >
                        <i class="bi bi-upload"></i>
                        Upload File
                      </button>
                      <button 
                        class="method-btn-chatroom"
                        :class="{ active: mediaMethod === 'url' }"
                        @click="mediaMethod = 'url'"
                      >
                        <i class="bi bi-link-45deg"></i>
                        From URL
                      </button>
                    </div>

                    <form @submit.prevent="sendMediaMessage">
                      <!-- Recipient (only for personal) -->
                      <div v-if="messageType === 'personal'" class="mb-3">
                        <label for="mediaRecipient" class="form-label">
                          <i class="bi bi-person-fill me-1"></i>
                          Recipient Phone Number
                        </label>
                        <input 
                          type="text" 
                          class="form-control form-control-chatroom" 
                          id="mediaRecipient"
                          v-model="mediaForm.recipient"
                          placeholder="628123456789"
                          required
                        >
                      </div>

                      <!-- Group info -->
                      <div v-if="messageType === 'group' && selectedGroup" class="alert alert-info alert-chatroom mb-3">
                        <i class="bi bi-people-fill me-2"></i>
                        <strong>Sending to:</strong> {{ selectedGroup.name }}
                      </div>

                      <!-- UPLOAD Method - dengan transition -->
                      <transition name="fade-slide-chatroom">
                        <div v-if="mediaMethod === 'upload'" class="mb-3 form-field-chatroom">
                            <label for="mediaFile" class="form-label">
                              <i class="bi bi-upload me-1"></i>
                              Upload Media
                            </label>
                            <input 
                              type="file" 
                              class="form-control form-control-chatroom" 
                              id="mediaFile"
                              @change="handleMediaUpload"
                              accept="image/*,video/*,audio/*"
                              required
                            >
                            <div class="form-text">
                              <i class="bi bi-info-circle me-1"></i>
                              <code>Images (JPG, PNG, GIF, WebP - 5MB) | Videos (MP4, MOV, AVI, MKV - 16MB) | Audio (MP3, OGG, M4A, Opus - 16MB) | Document (".pdf", ".doc", ".docx", ".xls", ".xlsx", ".txt", ".zip" - 100mb)</code>
                            </div>

                        </div>
                      </transition>

                      <!-- URL Method - dengan transition -->
                      <transition name="fade-slide-chatroom">
                        <div v-if="mediaMethod === 'url'" class="mb-3 form-field-chatroom">
                          <label for="mediaUrl" class="form-label">
                            <i class="bi bi-link-45deg me-1"></i>
                            Media URL
                          </label>
                          <input 
                            type="url" 
                            class="form-control form-control-chatroom" 
                            id="mediaUrl"
                            v-model="mediaForm.url"
                            placeholder="https://example.com/image.jpg"
                            required
                          >
                          <div class="form-text">
                            <i class="bi bi-info-circle me-1"></i>
                            <code> URL to image (5MB), video (16MB), or audio (16MB) or Document (100MB)</code>
                          </div>
                        </div>
                      </transition>


                      <div class="mb-3">
                        <label for="mediaCaption" class="form-label">
                          <i class="bi bi-chat-left-text me-1"></i>
                          Caption (Optional)
                        </label>
                        <textarea 
                          class="form-control form-control-chatroom" 
                          id="mediaCaption"
                          v-model="mediaForm.caption"
                          rows="4"
                          placeholder="Add a caption..."
                        ></textarea>
                      </div>

                      <div class="d-flex gap-2">
                        <button 
                          type="submit" 
                          class="btn btn-chatroom btn-primary-chatroom flex-grow-1"
                          :disabled="loading || (messageType === 'group' && !selectedGroup)"
                          :style="{ background: loading ? '' : `linear-gradient(135deg, ${themeColor} 0%, ${adjustColor(themeColor, 20)} 100%)` }"
                        >
                          <span v-if="loading">
                            <span class="spinner-border spinner-border-sm me-2"></span>
                            Uploading...
                          </span>
                          <span v-else>
                            <i class="bi bi-send-fill me-2"></i>
                            Send Media
                          </span>
                        </button>
                        <button 
                          type="button" 
                          class="btn btn-chatroom btn-secondary-chatroom"
                          @click="resetMediaForm"
                          :disabled="loading"
                          :style="{ background: themeColor, color: 'white', borderColor: themeColor }"
                        >
                          <i class="bi bi-arrow-counterclockwise"></i>
                        </button>
                      </div>
                    </form>
                  </div>

                  
                </div>

                <!-- Success/Error Messages -->
                <div v-if="successMessage" class="alert alert-success alert-chatroom mt-3 alert-dismissible fade show" role="alert">
                  <i class="bi bi-check-circle-fill me-2"></i>
                  {{ successMessage }}
                  <button type="button" class="btn-close" @click="successMessage = ''"></button>
                </div>
                <div v-if="errorMessage" class="alert alert-danger alert-chatroom mt-3 alert-dismissible fade show" role="alert">
                  <i class="bi bi-exclamation-triangle-fill me-2"></i>
                  {{ errorMessage }}
                  <button type="button" class="btn-close" @click="errorMessage = ''"></button>
                </div>
              </div>

            </div>
          </div>
        </div>

      </div>
    </section>
  </main>
</template>

<script>
import axios from 'axios';
import { Vue3Lottie } from 'vue3-lottie';
import { useSystem } from '@/composables/useSystem.js';

export default {
    components:{
      Vue3Lottie
    },
    setup() {
        const { themeColor } = useSystem();
        return { themeColor };
    },
    data() {
        return {
        instances: [],
        selectedInstance: null,
        activeTab: 'text',
        messagesSent: 0,
        messageType: 'personal', // 'personal' or 'group'
        mediaMethod: 'upload', // 'upload' or 'url'
        
        groups: [],
        selectedGroup: null,
        loadingGroups: false,
        
        textForm: {
            recipient: '',
            message: ''
        },
        
        mediaForm: {
            recipient: '',
            file: null,
            url: '',
            caption: ''
        },
        
        baseUrl: process.env.VUE_APP_BE_APP_BASE_URL,
        loading: false,
        successMessage: '',
        errorMessage: ''
        };
    },
    
    computed: {
        onlineInstances() {
            return Array.isArray(this.instances) ? this.instances.filter(inst => inst.status === 'online') : [];
        },
    },
    
    mounted() {
        this.fetchInstances();
    },
    
    methods: {
        adjustColor(hex, amt) {
            let usePound = false;
            if (hex[0] === "#") {
                hex = hex.slice(1);
                usePound = true;
            }
            const num = parseInt(hex, 16);
            let r = (num >> 16) + amt;
            if (r > 255) r = 255; else if (r < 0) r = 0;
            let b = ((num >> 8) & 0x00FF) + amt;
            if (b > 255) b = 255; else if (b < 0) b = 0;
            let g = (num & 0x0000FF) + amt;
            if (g > 255) g = 255; else if (g < 0) g = 0;
            return (usePound ? "#" : "") + (g | (b << 8) | (r << 16)).toString(16).padStart(6, '0');
        },
        
        async fetchInstances() {
          try {
              const response = await axios.get(`${this.baseUrl}/api/instances?all=true`);
              const data = response.data.data.instances || response.data.data || [];
              this.instances = Array.isArray(data) ? data : [];
          } catch (error) {
              console.error('Failed to fetch instances:', error);
              this.errorMessage = 'Failed to load instances. Please refresh the page.';
              this.instances = [];
          }
        },
        
        async fetchGroups() {
          if (!this.selectedInstance) return;
          
          this.loadingGroups = true;
          this.groups = [];
          
          try {
            const response = await axios.get(`${this.baseUrl}/api/groups/${this.selectedInstance.instanceId}`);
            this.groups = response.data.data.groups || [];
          } catch (error) {
            console.error('Failed to fetch groups:', error);
            this.errorMessage = 'Failed to load groups.';
          } finally {
            this.loadingGroups = false;
          }
        },
        
        onInstanceChange() {
          this.successMessage = '';
          this.errorMessage = '';
          this.activeTab = 'text';
          this.messagesSent = 0;
          this.messageType = 'personal';
          this.selectedGroup = null;
          this.groups = [];
          
          // Fetch groups untuk instance baru
          if (this.selectedInstance) {
            this.fetchGroups();
          }
        },
        
        formatDate(dateString) {
          if (!dateString) return 'N/A';
          const date = new Date(dateString);
          return date.toLocaleString('id-ID', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
          });
        },
        
        async sendTextMessage() {
          this.loading = true;
          this.successMessage = '';
          this.errorMessage = '';

          try {
            const payload = {
              message: this.textForm.message,
            };

            let endpoint = '';

            if (this.messageType === 'personal') {
              // kirim ke nomor personal
              payload.to = this.textForm.recipient;
              endpoint = `${this.baseUrl}/api/send/${this.selectedInstance.instanceId}`;
            } else {
              // kirim ke group
              payload.groupJid = this.selectedGroup.jid;
              endpoint = `${this.baseUrl}/api/send-group/${this.selectedInstance.instanceId}`;
            }

            await axios.post(endpoint, payload);

            this.successMessage = `Text message sent successfully to ${
              this.messageType === 'group'
                ? this.selectedGroup.name
                : this.textForm.recipient
            }!`;
            this.messagesSent++;
            this.resetTextForm();
          } catch (error) {
            console.error('Failed to send message:', error);
            this.errorMessage =
              error.response?.data?.message || 'Failed to send message.';
          } finally {
            this.loading = false;
          }
        },
        
        async sendMediaMessage() {
          this.loading = true;
          this.successMessage = '';
          this.errorMessage = '';
          
          try {
            if (this.mediaMethod === 'upload') {
                  // Send via FormData
                  const formData = new FormData();
                  formData.append('caption', this.mediaForm.caption);
                  formData.append('file', this.mediaForm.file);
                  
                  let endpoint = '';
                  
                  if (this.messageType === 'personal') {
                    formData.append('to', this.mediaForm.recipient);
                    endpoint = `${this.baseUrl}/api/send/${this.selectedInstance.instanceId}/media`;
                  } else {
                    formData.append('groupJid', this.selectedGroup.jid);
                    endpoint = `${this.baseUrl}/api/send-group/${this.selectedInstance.instanceId}/media`;
                  }
                  
                  await axios.post(endpoint, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                  });
            } else {
                // Send via URL
                const payload = {
                  mediaUrl: this.mediaForm.url,
                  caption: this.mediaForm.caption
                };
                
                let endpoint = '';
                
                if (this.messageType === 'personal') {
                  payload.to = this.mediaForm.recipient;
                  endpoint = `${this.baseUrl}/api/send/${this.selectedInstance.instanceId}/media-url`;
                } else {
                  payload.groupJid = this.selectedGroup.jid;
                  endpoint = `${this.baseUrl}/api/send-group/${this.selectedInstance.instanceId}/media-url`;
                }
                
                await axios.post(endpoint, payload);
            }
            
            this.successMessage = 'Media sent successfully!';
            this.messagesSent++;
            this.resetMediaForm();
            
          } catch (error) {
            console.error('Failed to send media:', error);
            this.errorMessage = error.response?.data?.message || 'Failed to send media.';
          } finally {
            this.loading = false;
          }
        },
        
  
        
        handleMediaUpload(event) {
          this.mediaForm.file = event.target.files[0];
        },
        
        resetTextForm() {
          if (this.messageType === 'personal') {
            this.textForm.recipient = '';
          }
          this.textForm.message = '';
        },
        
        resetMediaForm() {
          if (this.messageType === 'personal') {
            this.mediaForm.recipient = '';
          }
          this.mediaForm.file = null;
          this.mediaForm.url = '';
          this.mediaForm.caption = '';
          if(document.getElementById('mediaFile')) {
            document.getElementById('mediaFile').value = '';
          }
        },
     
    }
};
</script>

<style scoped>
</style>
